/**
 * RBAC Permissions screen API client (ADR-0046, migration 0157).
 *
 * Wraps the three endpoints under /v1/admin-review/permissions. Owner-only
 * server-side (requireCapability('manage_permissions'), itself hard-locked);
 * the tab's visibility is convenience, not security. Mirrors
 * src/lib/aiCaps.ts.
 */

import { writeFetch } from './writeFetch';
import { apiErrorFrom } from './api';

export interface PermissionCellState {
  capability: string;
  /** The static ROLE_CAPABILITIES default for this cell. */
  default_enabled: boolean;
  /** The effective value (override when present, else the default). */
  effective: boolean;
  /** True when an override row holds this cell away from its default. */
  overridden: boolean;
  /** True for the owner column + the two hard-locked capabilities. */
  locked: boolean;
  /** Human reason shown on hover/focus when locked. */
  locked_reason?: string;
}

export interface PermissionRoleColumn {
  role: string;
  /** Live count of active members holding this role (column header). */
  people_count: number;
  /** True for the owner column (every cell locked). */
  locked: boolean;
  entries: PermissionCellState[];
}

export interface PermissionsMatrixResponse {
  roles: PermissionRoleColumn[];
  request_id?: string;
}

export interface PermissionWriteResult {
  saved: true;
  changed: boolean;
  effective: boolean;
  overridden: boolean;
  request_id?: string;
}

async function requestJson(input: string, init: RequestInit = {}): Promise<unknown> {
  const r = await writeFetch(input, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!r.ok) throw await apiErrorFrom(r, `${init.method ?? 'GET'} ${input}`);
  return r.json();
}

const base = (slug: string): string =>
  `/api/t/${encodeURIComponent(slug)}/v1/admin-review/permissions`;

export const permissionsApi = {
  async list(tenantSlug: string, signal?: AbortSignal): Promise<PermissionsMatrixResponse> {
    return (await requestJson(base(tenantSlug), { signal })) as PermissionsMatrixResponse;
  },

  async setOverride(
    tenantSlug: string,
    role: string,
    capability: string,
    enabled: boolean,
  ): Promise<PermissionWriteResult> {
    return (await requestJson(
      `${base(tenantSlug)}/${encodeURIComponent(role)}/${encodeURIComponent(capability)}`,
      { method: 'PUT', body: JSON.stringify({ enabled }) },
    )) as PermissionWriteResult;
  },

  async revert(
    tenantSlug: string,
    role: string,
    capability: string,
  ): Promise<PermissionWriteResult> {
    return (await requestJson(
      `${base(tenantSlug)}/${encodeURIComponent(role)}/${encodeURIComponent(capability)}`,
      { method: 'DELETE' },
    )) as PermissionWriteResult;
  },
};
